import React, { useCallback, useEffect, useState } from 'react';
import { FiChevronRight } from 'react-icons/fi';
import CenterModalWindow from '../../layout/ReusableComponent/CenterModalWindow/CenterModalWindow';
import { StorageBoxFolderIcon } from '../utils/storageBoxFolderIcon';
import { STORAGE_BOX_ROOT_LABEL } from '../constants';

function folderDisabled(folderId, disabledFolderIds, selectedFolderIds) {
  if (!folderId) return false;
  if (disabledFolderIds?.has(folderId)) return true;
  if (selectedFolderIds?.has(folderId)) return true;
  return false;
}

export default function StorageBoxDestinationPickerModal({
  isOpen,
  mode = 'move',
  busy = false,
  rootId = null,
  rootLabel = STORAGE_BOX_ROOT_LABEL,
  loadFolders,
  disabledFolderIds,
  selectedFolderIds,
  itemCount = 0,
  onClose,
  onConfirm,
}) {
  const [stack, setStack] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentFolderId = stack.length ? stack[stack.length - 1].id : rootId;
  const currentFolderName = stack.length ? stack[stack.length - 1].name : rootLabel;

  const reset = useCallback(() => {
    setStack([]);
    setFolders([]);
    setError('');
  }, []);

  useEffect(() => {
    if (!isOpen) {
      reset();
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    loadFolders(currentFolderId)
      .then((rows) => {
        if (!cancelled) setFolders(rows || []);
      })
      .catch((e) => {
        if (!cancelled) {
          setFolders([]);
          setError(e.message || 'Could not load folders');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentFolderId, loadFolders, reset]);

  const title = mode === 'copy' ? 'Copy to' : 'Move to';
  const actionLabel = mode === 'copy' ? 'Copy here' : 'Move here';
  const destDisabled = folderDisabled(currentFolderId, disabledFolderIds, selectedFolderIds);

  const openFolder = (folder) => {
    if (folderDisabled(folder.itemId, disabledFolderIds, selectedFolderIds)) return;
    setStack((prev) => [...prev, { id: folder.itemId, name: folder.name }]);
  };

  const goToCrumb = (index) => {
    if (index < 0) {
      setStack([]);
      return;
    }
    setStack((prev) => prev.slice(0, index + 1));
  };

  const crumbs = [{ id: rootId, name: rootLabel }, ...stack];

  return (
    <CenterModalWindow
      isOpen={isOpen}
      onClose={onClose}
      width="min(480px, calc(100vw - 32px))"
      height="min(520px, calc(100vh - 48px))"
      contentClassName="sb-destination-picker-modal"
    >
      <div className="sb-destination-picker">
        <header className="sb-destination-picker__head">
          <h3 className="sb-destination-picker__title">{title}</h3>
          <p className="sb-destination-picker__subtitle">
            {itemCount === 1 ? '1 item' : `${itemCount} items`} — choose a folder
          </p>
        </header>

        <nav className="sb-destination-picker__crumbs" aria-label="Folder path">
          {crumbs.map((crumb, idx) => (
            <span key={`${crumb.id ?? 'root'}-${idx}`} className="sb-destination-picker__crumb-wrap">
              {idx > 0 ? <FiChevronRight className="sb-destination-picker__crumb-sep" aria-hidden /> : null}
              <button
                type="button"
                className="sb-destination-picker__crumb"
                onClick={() => goToCrumb(idx - 1)}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </nav>

        <div className="sb-destination-picker__list-wrap" data-modal-scroll-port>
          {loading ? <p className="sb-destination-picker__status">Loading folders…</p> : null}
          {error ? <p className="sb-destination-picker__status sb-destination-picker__status--error">{error}</p> : null}
          {!loading && !error && !folders.length ? (
            <p className="sb-destination-picker__status">No subfolders here.</p>
          ) : null}
          <ul className="sb-destination-picker__list">
            {folders.map((folder) => {
              const disabled = folderDisabled(folder.itemId, disabledFolderIds, selectedFolderIds);
              return (
                <li key={folder.itemId}>
                  <button
                    type="button"
                    className={`sb-destination-picker__row${disabled ? ' sb-destination-picker__row--disabled' : ''}`}
                    disabled={disabled}
                    onClick={() => openFolder(folder)}
                  >
                    <StorageBoxFolderIcon name={folder.name} className="sb-destination-picker__folder-icon" />
                    <span className="sb-destination-picker__row-name">{folder.name}</span>
                    {!disabled ? <FiChevronRight aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <footer className="sb-destination-picker__actions">
          <button
            type="button"
            className="sb-secondary-btn sb-destination-picker__btn"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sb-primary-btn sb-destination-picker__btn"
            disabled={busy || destDisabled}
            onClick={() => onConfirm?.(currentFolderId, currentFolderName)}
          >
            {busy ? 'Please wait…' : actionLabel}
          </button>
        </footer>
        {destDisabled ? (
          <p className="sb-destination-picker__hint">This folder cannot be used as the destination.</p>
        ) : null}
      </div>
    </CenterModalWindow>
  );
}
